import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  fetchProgramNewsUpdates,
  type ProgramNewsDeliveryClass,
  type ProgramNewsUpdate,
  type ProgramNewsUpdateCounts,
} from "@/services/adminProgramNews";
import EventCard from "./EventCard";
import EventClassTabs from "./EventClassTabs";
import { EMPTY_STATE } from "./eventEmptyState";
import UnreviewedToggle from "./UnreviewedToggle";
import TeamIdFilter from "./TeamIdFilter";
import PanelIntro from "./PanelIntro";
import { PANEL_INTROS } from "./panelIntros";

const PAGE_LIMIT = 50;
const DEBOUNCE_MS = 300;

const EventsPanel = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [deliveryClass, setDeliveryClass] = useState<ProgramNewsDeliveryClass>("alert");
  const [teamInput, setTeamInput] = useState("");
  const teamId = useDebouncedValue(teamInput.trim(), DEBOUNCE_MS);
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  const [counts, setCounts] = useState<Partial<ProgramNewsUpdateCounts>>({});
  const [updates, setUpdates] = useState<ProgramNewsUpdate[]>([]);

  // `quiet` refreshes in place so an open detail sheet is not torn down.
  const load = useCallback(
    async (quiet = false) => {
      try {
        if (!quiet) setLoading(true);
        const response = await fetchProgramNewsUpdates({
          deliveryClass,
          teamId: teamId || undefined,
          reviewed: unreviewedOnly ? "no" : undefined,
          limit: PAGE_LIMIT,
        });
        setCounts(response.counts ?? {});
        setUpdates(Array.isArray(response.updates) ? response.updates : []);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        toast({ title: "Failed to load events", description: message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    },
    [deliveryClass, teamId, unreviewedOnly, toast]
  );

  useEffect(() => {
    load();
  }, [load]);

  const refreshQuietly = useCallback(() => {
    load(true);
  }, [load]);

  return (
    <div className="space-y-4">
      <PanelIntro intro={PANEL_INTROS.events} />
      <div className="flex flex-wrap items-center gap-3">
        <EventClassTabs counts={counts} active={deliveryClass} onChange={setDeliveryClass} />
        <TeamIdFilter value={teamInput} onChange={setTeamInput} />
        <UnreviewedToggle
          id="program-news-events-unreviewed"
          checked={unreviewedOnly}
          onChange={setUnreviewedOnly}
        />
      </div>
      {loading ? (
        <div className="py-8 text-center text-muted-foreground">Loading events...</div>
      ) : updates.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">{EMPTY_STATE[deliveryClass]}</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {updates.map(update => (
            <EventCard key={update._id} update={update} onReviewChanged={refreshQuietly} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsPanel;
